import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type {
  InspectionNode,
  InspectionNodeInsert,
  InspectionNodeWithDetails,
} from '../../../features/jobs/types/inspectionNode'
import { nodeService } from '../../../features/jobs/services/nodeService'
import { logout } from './authSlice'

// Type for creating nodes - node_number is generated by the service
type CreateNodeData = Omit<InspectionNodeInsert, 'node_number'>

interface NodesState {
  items: InspectionNodeWithDetails[]
  currentNode: InspectionNodeWithDetails | null
  loading: boolean
  error: string | null
  currentJobId: string | null
}

const initialState: NodesState = {
  items: [],
  currentNode: null,
  loading: false,
  error: null,
  currentJobId: null,
}

// Async thunks
export const fetchNodesByJobId = createAsyncThunk(
  'nodes/fetchNodesByJobId',
  async (jobId: string) => {
    return await nodeService.getNodesByJobId(jobId)
  }
)

export const fetchNodeById = createAsyncThunk('nodes/fetchNodeById', async (id: string) => {
  return await nodeService.getNode(id)
})

export const createNode = createAsyncThunk('nodes/createNode', async (nodeData: CreateNodeData) => {
  return await nodeService.createNode(nodeData as InspectionNodeInsert)
})

export const updateNode = createAsyncThunk(
  'nodes/updateNode',
  async ({ id, updates }: { id: string; updates: Partial<InspectionNode> }) => {
    return await nodeService.updateNode(id, updates)
  }
)

export const deleteNode = createAsyncThunk('nodes/deleteNode', async (id: string) => {
  await nodeService.deleteNode(id)
  return id
})

// Slice
const nodesSlice = createSlice({
  name: 'nodes',
  initialState,
  reducers: {
    setCurrentJobId: (state, action: PayloadAction<string>) => {
      state.currentJobId = action.payload
    },
    clearCurrentNode: (state) => {
      state.currentNode = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearNodes: (state) => {
      state.items = []
      state.currentNode = null
      state.currentJobId = null
    },
  },
  extraReducers: (builder) => {
    // Fetch nodes by job ID
    builder
      .addCase(fetchNodesByJobId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNodesByJobId.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload
      })
      .addCase(fetchNodesByJobId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch trees'
      })

    // Fetch node by ID
    builder
      .addCase(fetchNodeById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNodeById.fulfilled, (state, action) => {
        state.loading = false
        state.currentNode = action.payload
      })
      .addCase(fetchNodeById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch tree'
      })

    // Create node
    builder
      .addCase(createNode.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createNode.fulfilled, (state, action) => {
        state.loading = false
        state.items.push(action.payload as InspectionNodeWithDetails)
      })
      .addCase(createNode.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create tree'
      })

    // Update node
    builder
      .addCase(updateNode.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateNode.fulfilled, (state, action) => {
        state.loading = false
        const index = state.items.findIndex((node) => node.id === action.payload.id)
        if (index !== -1) {
          state.items[index] = action.payload as InspectionNodeWithDetails
        }
        if (state.currentNode?.id === action.payload.id) {
          state.currentNode = action.payload as InspectionNodeWithDetails
        }
      })
      .addCase(updateNode.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update tree'
      })

    // Delete node
    builder
      .addCase(deleteNode.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteNode.fulfilled, (state, action) => {
        state.loading = false
        state.items = state.items.filter((node) => node.id !== action.payload)
        if (state.currentNode?.id === action.payload) {
          state.currentNode = null
        }
      })
      .addCase(deleteNode.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete tree'
      })

    // Clear nodes state on logout
    builder.addCase(logout.fulfilled, (state) => {
      state.items = []
      state.currentNode = null
      state.loading = false
      state.error = null
      state.currentJobId = null
    })
  },
})

export const { setCurrentJobId, clearCurrentNode, clearError, clearNodes } = nodesSlice.actions
export const nodesReducer = nodesSlice.reducer
